/**
 * Entity interpolation for remote players + the ball. (§2.5)
 *
 * Pure logic — no Three.js, no DOM, no clock reads. The caller passes time in.
 * That is what makes the starvation behaviour testable, and starvation is the
 * only part of this file that is hard.
 *
 * ── The idea ───────────────────────────────────────────────────────────────
 *
 * Remote entities are drawn at `serverTime - INTERPOLATION_DELAY_MS`. We are
 * deliberately rendering the past: at 20Hz snapshots, two packets is 100ms, so
 * a render time 100ms behind the newest snapshot almost always sits BETWEEN two
 * snapshots we already hold, and we interpolate rather than guess. Jitter and a
 * single dropped packet are then invisible instead of being a stutter.
 *
 * ── Why the buffer stores WorldStates, not SnapshotFrames ──────────────────
 *
 * A SnapshotFrame holds quantized wire integers, and its player flag layout
 * (active/team/possessing/sentOff bits) is PRIVATE to snapshot.ts. Unpacking it
 * here would mean copying a protocol detail into the client, where it would
 * silently rot the day snapshot.ts appends a flag. So each buffered snapshot is
 * decoded once, through the frozen `dequantizeFrameInto`, into a pooled
 * WorldState. We interpolate plain f64s and never learn the wire layout at all.
 *
 * The non-snapshot fields of those pooled worlds (rng, timers, offside) are
 * meaningless — `dequantizeFrameInto` leaves them untouched. Never read them.
 *
 * ── Hermite, not lerp ──────────────────────────────────────────────────────
 *
 * §2.5 specifies Hermite/Catmull-Rom using the velocity the snapshot already
 * carries. Plain lerp between two positions 50ms apart visibly corners: a
 * sprinting player's curved path becomes a polyline, and the ball's flight
 * becomes a sequence of straight segments with a kink at every snapshot. The
 * velocities are already on the wire; using them costs nothing and is the
 * difference between "networked" and "smooth".
 */

import {
  FIXED_DT,
  INTERPOLATION_DELAY_MS,
  MAX_EXTRAPOLATION_MS,
  MAX_PLAYERS,
  allocWorldLike,
  type WorldState,
} from '@fg/shared/sim';
import { dequantizeFrameInto, type SnapshotFrame } from '@fg/shared/net';
import { wrapAngle } from '@fg/shared/math';

/** How the current sample was produced — drives the dev overlay + tests. */
export const enum InterpMode {
  /** Render time sits between two buffered snapshots. The happy path. */
  Interpolated = 0,
  /** Buffer starved: dead-reckoning from the newest snapshot's velocity (§2.5). */
  Extrapolated = 1,
  /** Starved beyond MAX_EXTRAPOLATION_MS. Held still — guessing further lies. */
  Frozen = 2,
  /** Render time predates the oldest snapshot (just connected). Clamped. */
  Clamped = 3,
  /** Nothing buffered at all. `view` is untouched. */
  Empty = 4,
}

/** `InterpMode` is a const enum, so it has no reverse mapping to index. */
export function interpModeName(m: InterpMode): string {
  switch (m) {
    case InterpMode.Interpolated: return 'interpolated';
    case InterpMode.Extrapolated: return 'extrapolated';
    case InterpMode.Frozen: return 'FROZEN';
    case InterpMode.Clamped: return 'clamped';
    case InterpMode.Empty: return 'empty';
    default: return 'unknown';
  }
}

/**
 * Snapshots kept for interpolation. At 20Hz this is 800ms of history — far more
 * than the 100ms delay needs, but it costs ~16 pooled worlds and it is what lets
 * a late burst of packets still find a bracketing pair instead of extrapolating.
 */
export const SNAPSHOT_BUFFER_CAPACITY = 16;

// ---------------------------------------------------------------------------
// Server clock
// ---------------------------------------------------------------------------

/**
 * Estimates "what tick is the server on right now" from snapshot arrivals.
 *
 * Deliberately does NOT use RTT. A ping measures a round trip through two queues
 * and tells you little about one-way snapshot delay, and connection.ts's Ping is
 * an invented codec the server may not even answer. Arrival times of snapshots
 * we are already receiving are free and measure exactly the thing we need.
 *
 * ── The asymmetry is the whole algorithm ───────────────────────────────────
 *
 * offset = serverTime - localTime. A packet delayed by the network makes the
 * server look older than it is, so it yields a LOW offset. A packet that got
 * through quickly yields a HIGH one, closer to the truth. So: jump UP instantly
 * (that packet proved the server is at least this far along) and drift DOWN
 * slowly (one late packet is jitter; sustained lateness is a real latency
 * change). Averaging both directions would instead track the MEAN delay and
 * leave the render time permanently sitting in a jitter trough.
 */
export class ServerClock {
  private offsetMs = 0;
  private started = false;

  /** Per-sample decay when latency genuinely rises. ~1.5s to converge. */
  private static readonly DRIFT = 0.03;

  get initialized(): boolean {
    return this.started;
  }

  /** Feed a snapshot's server tick and the local time it arrived. */
  sample(serverTick: number, localNowMs: number): void {
    const serverMs = serverTick * FIXED_DT * 1000;
    const sample = serverMs - localNowMs;
    if (!this.started) {
      this.offsetMs = sample;
      this.started = true;
      return;
    }
    if (sample > this.offsetMs) this.offsetMs = sample;
    else this.offsetMs += (sample - this.offsetMs) * ServerClock.DRIFT;
  }

  /** Best estimate of server time, in ms, at local time `localNowMs`. */
  serverTimeMs(localNowMs: number): number {
    return localNowMs + this.offsetMs;
  }

  /** The time remote entities should be drawn at (§2.5). */
  renderTimeMs(localNowMs: number): number {
    return this.serverTimeMs(localNowMs) - INTERPOLATION_DELAY_MS;
  }

  reset(): void {
    this.started = false;
    this.offsetMs = 0;
  }
}

// ---------------------------------------------------------------------------
// Interpolated view
// ---------------------------------------------------------------------------

/**
 * The renderable result. Structure-of-arrays, allocated once, overwritten in
 * place every frame (§15.7). Consumers must gate on `active` — an inactive
 * slot's kinematics are meaningless (snapshot.ts says so explicitly).
 */
export interface InterpolatedView {
  /** Fractional server tick this view represents. */
  tick: number;
  mode: InterpMode;
  active: Uint8Array;
  team: Uint8Array;
  px: Float64Array;
  py: Float64Array;
  pz: Float64Array;
  vx: Float64Array;
  vy: Float64Array;
  vz: Float64Array;
  yaw: Float64Array;
  stamina: Float64Array;
  anim: Uint8Array;
  /**
   * The sim's `animExecId` (§11) for the frame this view shows.
   *
   * Taken from the OLDER frame alongside `anim`, for the same reason and it
   * MATTERS MORE here: the pair must stay consistent. Reading the state from A
   * and the id from B would manufacture an execution that never existed, and the
   * director would restart the clip on a frame where the sim did nothing.
   */
  animExec: Uint8Array;
  animBlend: Float64Array;
  possessing: Uint8Array;
  sentOff: Uint8Array;
  ballX: number; ballY: number; ballZ: number;
  ballVX: number; ballVY: number; ballVZ: number;
  ballQx: number; ballQy: number; ballQz: number; ballQw: number;
  ballPossessedBy: number;
  /** Mirrors `BallState.powerKick` (net/snapshot.ts's `BF_POWERKICK`) — a
   *  discrete flag, not interpolated, same treatment as `ballPossessedBy`. */
  ballPowerKick: boolean;
}

export function createInterpolatedView(): InterpolatedView {
  return {
    tick: 0,
    mode: InterpMode.Empty,
    active: new Uint8Array(MAX_PLAYERS),
    team: new Uint8Array(MAX_PLAYERS),
    px: new Float64Array(MAX_PLAYERS),
    py: new Float64Array(MAX_PLAYERS),
    pz: new Float64Array(MAX_PLAYERS),
    vx: new Float64Array(MAX_PLAYERS),
    vy: new Float64Array(MAX_PLAYERS),
    vz: new Float64Array(MAX_PLAYERS),
    yaw: new Float64Array(MAX_PLAYERS),
    stamina: new Float64Array(MAX_PLAYERS),
    anim: new Uint8Array(MAX_PLAYERS),
    animExec: new Uint8Array(MAX_PLAYERS),
    animBlend: new Float64Array(MAX_PLAYERS),
    possessing: new Uint8Array(MAX_PLAYERS),
    sentOff: new Uint8Array(MAX_PLAYERS),
    ballX: 0, ballY: 0, ballZ: 0,
    ballVX: 0, ballVY: 0, ballVZ: 0,
    ballQx: 0, ballQy: 0, ballQz: 0, ballQw: 1,
    ballPossessedBy: -1,
    ballPowerKick: false,
  };
}

// ---------------------------------------------------------------------------
// Interpolation maths
// ---------------------------------------------------------------------------

/**
 * Cubic Hermite on one axis. `dt` is the wall time between the two samples, in
 * seconds — it scales the tangents, and getting it wrong (passing the
 * normalized t, say) turns the curve into a wild overshoot rather than an
 * obviously broken result.
 */
function hermite(p0: number, v0: number, p1: number, v1: number, t: number, dt: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return h00 * p0 + h10 * dt * v0 + h01 * p1 + h11 * dt * v1;
}

/**
 * Shortest-arc angle interpolation — slerp for the 1-D case (§2.5 "rotation with
 * slerp"). Players store a single yaw, not a quaternion (types.ts: "players are
 * upright — a single angle beats a quat"), so a full quaternion slerp here would
 * be ceremony around the same result. Going through wrapAngle is what stops a
 * player crossing ±π from spinning 350° the long way round.
 */
function lerpAngle(a: number, b: number, t: number): number {
  return wrapAngle(a + wrapAngle(b - a) * t);
}

/** Quaternion slerp for the ball's spin (§2.5). Falls back to nlerp when close. */
function slerpQuat(
  ax: number, ay: number, az: number, aw: number,
  bx: number, by: number, bz: number, bw: number,
  t: number,
  out: { x: number; y: number; z: number; w: number },
): void {
  let dot = ax * bx + ay * by + az * bz + aw * bw;
  let cx = bx, cy = by, cz = bz, cw = bw;
  // q and -q are the same rotation; pick the near one or we slerp the long way.
  if (dot < 0) {
    dot = -dot;
    cx = -bx; cy = -by; cz = -bz; cw = -bw;
  }
  if (dot > 0.9995) {
    // Nearly parallel: sin(theta) -> 0 and the slerp weights blow up. nlerp is
    // indistinguishable at this angle and is numerically safe.
    const x = ax + (cx - ax) * t;
    const y = ay + (cy - ay) * t;
    const z = az + (cz - az) * t;
    const w = aw + (cw - aw) * t;
    const len = Math.hypot(x, y, z, w) || 1;
    out.x = x / len; out.y = y / len; out.z = z / len; out.w = w / len;
    return;
  }
  const theta = Math.acos(dot);
  const sinTheta = Math.sin(theta);
  const s0 = Math.sin((1 - t) * theta) / sinTheta;
  const s1 = Math.sin(t * theta) / sinTheta;
  out.x = s0 * ax + s1 * cx;
  out.y = s0 * ay + s1 * cy;
  out.z = s0 * az + s1 * cz;
  out.w = s0 * aw + s1 * cw;
}

/** Module-scope scratch so slerping the ball allocates nothing. (§15.7) */
const quatScratch = { x: 0, y: 0, z: 0, w: 1 };

// ---------------------------------------------------------------------------
// The buffer
// ---------------------------------------------------------------------------

export class InterpolationBuffer {
  /** Pooled decoded snapshots, newest last. Length <= SNAPSHOT_BUFFER_CAPACITY. */
  private readonly pool: WorldState[] = [];
  /** Indices into `pool`, oldest first. A plain array of numbers — never sorted
   *  in the hot path; snapshots arrive in tick order (see `push`). */
  private readonly order: number[] = [];
  private free: number[] = [];

  constructor(template: WorldState, capacity: number = SNAPSHOT_BUFFER_CAPACITY) {
    for (let i = 0; i < capacity; i++) {
      this.pool.push(allocWorldLike(template));
      this.free.push(i);
    }
  }

  get size(): number {
    return this.order.length;
  }

  /** Server tick of the newest buffered snapshot, or -1 when empty. */
  get newestTick(): number {
    const last = this.order[this.order.length - 1];
    return last === undefined ? -1 : this.pool[last]!.tick;
  }

  /** Server tick of the oldest buffered snapshot, or -1 when empty. */
  get oldestTick(): number {
    const first = this.order[0];
    return first === undefined ? -1 : this.pool[first]!.tick;
  }

  clear(): void {
    this.free = this.pool.map((_, i) => i);
    this.order.length = 0;
  }

  /**
   * Decode and buffer a snapshot. `frame` is borrowed — we copy out of it here,
   * which is why connection.ts may hand out its live baseline.
   *
   * A frame at or before the newest buffered tick is DROPPED. On the current
   * ordered transport that cannot happen; on an unreliable one it is a
   * duplicate or a straggler, and either way inserting it would corrupt the
   * ordering the sampler depends on for correctness, not just for speed.
   */
  push(frame: SnapshotFrame): void {
    if (this.order.length > 0 && frame.tick <= this.newestTick) return;

    let idx = this.free.pop();
    if (idx === undefined) {
      // Full: evict the oldest. Its data is already behind the render time.
      idx = this.order.shift()!;
    }
    dequantizeFrameInto(frame, this.pool[idx]!);
    this.order.push(idx);
  }

  /**
   * Fill `out` with the world as it should be drawn at `renderTimeMs` (server
   * clock). Returns the mode so the caller can surface starvation.
   */
  sample(renderTimeMs: number, out: InterpolatedView): InterpMode {
    if (this.order.length === 0) {
      out.mode = InterpMode.Empty;
      return InterpMode.Empty;
    }

    const renderTick = renderTimeMs / (FIXED_DT * 1000);

    // Single snapshot, or render time past the newest: dead-reckon (§2.5).
    if (this.order.length === 1 || renderTick >= this.newestTick) {
      return this.extrapolate(renderTick, out);
    }

    // Behind the oldest: we are still filling the buffer after a connect, or the
    // clock jumped. Clamp rather than extrapolate backwards.
    if (renderTick <= this.oldestTick) {
      this.copyFrom(this.pool[this.order[0]!]!, out);
      out.tick = this.oldestTick;
      out.mode = InterpMode.Clamped;
      return InterpMode.Clamped;
    }

    // Find the bracketing pair. The buffer holds ~16 entries and is ordered, so
    // a linear scan from the newest end finds it in one or two steps.
    for (let i = this.order.length - 1; i > 0; i--) {
      const b = this.pool[this.order[i]!]!;
      const a = this.pool[this.order[i - 1]!]!;
      if (renderTick >= a.tick && renderTick <= b.tick) {
        this.interpolatePair(a, b, renderTick, out);
        out.mode = InterpMode.Interpolated;
        return InterpMode.Interpolated;
      }
    }

    // Unreachable given the bounds checks above, but a gap in the ticks (a
    // snapshot the server never sent) would land here. Extrapolating from the
    // newest is the safe answer.
    return this.extrapolate(renderTick, out);
  }

  /** §2.5: extrapolate from the last known velocity for at most 200ms, then freeze. */
  private extrapolate(renderTick: number, out: InterpolatedView): InterpMode {
    const newest = this.pool[this.order[this.order.length - 1]!]!;
    const aheadS = (renderTick - newest.tick) * FIXED_DT;
    const maxS = MAX_EXTRAPOLATION_MS / 1000;

    // Clamping (rather than stopping) is what "then freeze" means: the entity
    // coasts for 200ms and then holds the position it reached. It does NOT snap
    // back to the last snapshot, which would be a visible lurch backwards at
    // exactly the moment the connection is already struggling.
    const dt = aheadS <= 0 ? 0 : aheadS > maxS ? maxS : aheadS;
    const frozen = aheadS > maxS;

    const p = newest.players;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      out.active[i] = p.active[i]!;
      out.team[i] = p.team[i]!;
      out.px[i] = p.px[i]! + p.vx[i]! * dt;
      out.py[i] = p.py[i]! + p.vy[i]! * dt;
      out.pz[i] = p.pz[i]! + p.vz[i]! * dt;
      out.vx[i] = p.vx[i]!;
      out.vy[i] = p.vy[i]!;
      out.vz[i] = p.vz[i]!;
      out.yaw[i] = p.yaw[i]!;
      out.stamina[i] = p.stamina[i]!;
      out.anim[i] = p.anim[i]!;
      out.animExec[i] = p.animExecId[i]!;
      out.animBlend[i] = p.animBlend[i]!;
      out.possessing[i] = p.possessing[i]!;
      out.sentOff[i] = p.sentOff[i]!;
    }
    const b = newest.ball;
    out.ballX = b.px + b.vx * dt;
    out.ballY = b.py + b.vy * dt;
    out.ballZ = b.pz + b.vz * dt;
    out.ballVX = b.vx; out.ballVY = b.vy; out.ballVZ = b.vz;
    out.ballQx = b.qx; out.ballQy = b.qy; out.ballQz = b.qz; out.ballQw = b.qw;
    out.ballPossessedBy = b.possessedBy;
    out.ballPowerKick = b.powerKick;
    out.tick = newest.tick + dt / FIXED_DT;

    const mode = frozen ? InterpMode.Frozen : InterpMode.Extrapolated;
    out.mode = mode;
    return mode;
  }

  private interpolatePair(a: WorldState, b: WorldState, renderTick: number, out: InterpolatedView): void {
    const span = b.tick - a.tick;
    const t = span <= 0 ? 0 : (renderTick - a.tick) / span;
    const dt = span * FIXED_DT;

    const pa = a.players;
    const pb = b.players;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      // Discrete fields come from the OLDER frame: it is the state that was
      // actually true across this interval. Taking the newer one would show a
      // tackle animation up to 50ms before the tackle happened.
      out.active[i] = pa.active[i]!;
      out.team[i] = pa.team[i]!;
      out.possessing[i] = pa.possessing[i]!;
      out.sentOff[i] = pa.sentOff[i]!;
      out.anim[i] = pa.anim[i]!;
      out.animExec[i] = pa.animExecId[i]!;

      out.px[i] = hermite(pa.px[i]!, pa.vx[i]!, pb.px[i]!, pb.vx[i]!, t, dt);
      out.py[i] = hermite(pa.py[i]!, pa.vy[i]!, pb.py[i]!, pb.vy[i]!, t, dt);
      out.pz[i] = hermite(pa.pz[i]!, pa.vz[i]!, pb.pz[i]!, pb.vz[i]!, t, dt);
      out.vx[i] = pa.vx[i]! + (pb.vx[i]! - pa.vx[i]!) * t;
      out.vy[i] = pa.vy[i]! + (pb.vy[i]! - pa.vy[i]!) * t;
      out.vz[i] = pa.vz[i]! + (pb.vz[i]! - pa.vz[i]!) * t;
      out.yaw[i] = lerpAngle(pa.yaw[i]!, pb.yaw[i]!, t);
      out.stamina[i] = pa.stamina[i]! + (pb.stamina[i]! - pa.stamina[i]!) * t;
      // Blend time only interpolates within one animation; across a change it
      // restarts, and lerping 0.9 -> 0.1 would play the blend backwards.
      out.animBlend[i] =
        pa.anim[i]! === pb.anim[i]!
          ? pa.animBlend[i]! + (pb.animBlend[i]! - pa.animBlend[i]!) * t
          : pa.animBlend[i]!;
    }

    const ba = a.ball;
    const bb = b.ball;
    out.ballX = hermite(ba.px, ba.vx, bb.px, bb.vx, t, dt);
    out.ballY = hermite(ba.py, ba.vy, bb.py, bb.vy, t, dt);
    out.ballZ = hermite(ba.pz, ba.vz, bb.pz, bb.vz, t, dt);
    out.ballVX = ba.vx + (bb.vx - ba.vx) * t;
    out.ballVY = ba.vy + (bb.vy - ba.vy) * t;
    out.ballVZ = ba.vz + (bb.vz - ba.vz) * t;
    slerpQuat(ba.qx, ba.qy, ba.qz, ba.qw, bb.qx, bb.qy, bb.qz, bb.qw, t, quatScratch);
    out.ballQx = quatScratch.x; out.ballQy = quatScratch.y;
    out.ballQz = quatScratch.z; out.ballQw = quatScratch.w;
    out.ballPossessedBy = ba.possessedBy;
    out.ballPowerKick = ba.powerKick;
    out.tick = renderTick;
  }

  private copyFrom(w: WorldState, out: InterpolatedView): void {
    const p = w.players;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      out.active[i] = p.active[i]!;
      out.team[i] = p.team[i]!;
      out.px[i] = p.px[i]!; out.py[i] = p.py[i]!; out.pz[i] = p.pz[i]!;
      out.vx[i] = p.vx[i]!; out.vy[i] = p.vy[i]!; out.vz[i] = p.vz[i]!;
      out.yaw[i] = p.yaw[i]!;
      out.stamina[i] = p.stamina[i]!;
      out.anim[i] = p.anim[i]!;
      out.animExec[i] = p.animExecId[i]!;
      out.animBlend[i] = p.animBlend[i]!;
      out.possessing[i] = p.possessing[i]!;
      out.sentOff[i] = p.sentOff[i]!;
    }
    const b = w.ball;
    out.ballX = b.px; out.ballY = b.py; out.ballZ = b.pz;
    out.ballVX = b.vx; out.ballVY = b.vy; out.ballVZ = b.vz;
    out.ballQx = b.qx; out.ballQy = b.qy; out.ballQz = b.qz; out.ballQw = b.qw;
    out.ballPossessedBy = b.possessedBy;
    out.ballPowerKick = b.powerKick;
  }
}
